import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import * as Sentry from '@sentry/react';
import { getCsrfToken } from '../../Shared/API/config';

const fetchPublic = fetchBaseQuery({
  baseUrl: '/api',
  prepareHeaders: headers => {
    const csrfToken = getCsrfToken();
    if (csrfToken) headers.set('X-CSRFToken', csrfToken);
    return headers;
  },
});

/**
 * A deliberately quiet base query for the public one-time-link pages (invite
 * claim, password reset).
 *
 * The shared `baseQuery` console-logs every failed request's args — here the
 * token and the new password — and attaches the signed-in user's credentials.
 * This one logs nothing, sends no Authorization header, and reports only
 * server faults, by path. `app` tags the Sentry report with the page it came
 * from.
 */
export const createQuietBaseQuery =
  (app: string): typeof fetchPublic =>
  async (args, api, extraOptions) => {
    const result = await fetchPublic(args, api, extraOptions);
    const status = result.error?.status;
    if (result.error && (typeof status !== 'number' || status >= 500)) {
      const path = typeof args === 'string' ? args : args.url;
      Sentry.captureException(new Error(`API ${status} POST /api${path}`), {
        level: 'error',
        fingerprint: ['api-error', 'POST', path, String(status)],
        tags: { 'api.status': String(status), 'api.path': path, app },
      });
    }
    return result;
  };
